import type { SxProps, Theme } from '@mui/material';
import { Typography } from '@mui/material';
import { Skeleton } from '@mui/material';
import { isNotNullOrUndefined } from '@weft/utils';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import type { VyPageLayoutProps } from '../../../components/new-design/layout/VyPageLayout';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { useCustomerInvoicePageData } from '../../../hooks/requests/use-customer-invoice-page-data';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { InvoiceBillingStatusLabel } from '../InvoicesPage/invoices-page-components/InvoiceBillingStatusLabel';
import { CustomerInvoicePageTabsAction } from './components/CustomerInvoicePageTabsAction';
import { CUSTOMER_INVOICE_PAGE_TAB_OPTIONS,
  useCustomerInvoicePageTabs } from './customer-invoice-page-hooks/use-customer-invoice-page-tabs';

export const CustomerInvoicePage: React.FC<PropsWithChildren> = ({ children }) => {
  const { customerId, invoiceId } = useValidatedParams<['customerId', 'invoiceId']>('customerId', 'invoiceId');

  const location = useLocation();
  const isFromInvoices = location.pathname.startsWith('/invoices');

  const { data, isLoading } = useCustomerInvoicePageData(customerId, invoiceId);
  const [tabs, currentTab] = useCustomerInvoicePageTabs();

  const layoutStyle: SxProps<Theme> = useMemo(() => {
    if (currentTab === CUSTOMER_INVOICE_PAGE_TAB_OPTIONS.PreviewInvoice) {
      return {
        padding: 0,
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
      };
    }

    return {
      padding: 0,
      paddingBottom: '48px',
    };
  }, [currentTab]);

  const path = useMemo((): VyPageLayoutProps['path'] => {
    if (isLoading) {
      return [(
        <Skeleton
          variant="text"
          width={200}
        />
      )];
    }

    return isFromInvoices ?
      [
        {
          label: 'Invoices',
          href: `/customers/${customerId}/invoices`,
        },
        data?.invoice.name,
      ]
      : [
        {
          label: 'Customers',
          href: '/customers',
        },
        {
          label: data?.customer.name,
          href: `/customers/${customerId}`,
        },
        {
          label: 'Invoices',
          href: `/customers/${customerId}/invoices`,
        },
        data?.invoice.name,
      ];
  }, [customerId, data, isLoading, isFromInvoices]);

  return (
    <VyPageLayout
      path={path}
      title={(
        <>
          {isNotNullOrUndefined(data) && (
            <>
              <Typography variant="h5">
                {`${data.customer.name} - ${data.invoice.name}`}
              </Typography>

              <InvoiceBillingStatusLabel billingStatus={data.invoice.billingStatus}/>
            </>
          )}
        </>
      )}
      tabs={tabs}
      currentTab={currentTab}
      sx={layoutStyle}
      tabsAction={currentTab === CUSTOMER_INVOICE_PAGE_TAB_OPTIONS.PreviewInvoice && (
        <CustomerInvoicePageTabsAction/>
      )}
      showBackButton
    >
      {children}
    </VyPageLayout>
  );
};